import { createCompanySpecificIndustryApi } from "@/api/services/global/master";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Modal } from "@/components/elements/Modal";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { CreateCompanySpecificIndustryFormTable } from "@/features/master-data/components/CompanySpecificIndustry/CreateCompanySpecificIndustryFormTable";
import { masterDataCreateCompanySpecificIndustrySchema } from "@/features/master-data/schema/masterDataCompanySpecificIndustrySchema";
import { TMasterCompanySpecificIndustryCreateForm } from "@/features/master-data/types/masterDataCompanySpecificIndustryForm";
import { setErrorForm, showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormLabel, Grid, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";

type TCreateCompanySpecificIndustryFormModalProps = {
  onClose: () => void;
  isOpen: boolean;
};

const defaultFormValues: TMasterCompanySpecificIndustryCreateForm = {
  companySpecificIndustry: "",
  companySpecificBusinessClassifications: [
    {
      companySpecificBusinessClassificationName: "",
    },
  ],
};

export const CreateCompanySpecificIndustryFormModal: FC<TCreateCompanySpecificIndustryFormModalProps> = ({ onClose, isOpen }) => {
  const queryClient = useQueryClient();

  const methods = useForm<TMasterCompanySpecificIndustryCreateForm>({
    resolver: yupResolver(masterDataCreateCompanySpecificIndustrySchema),
    mode: "onBlur",
    defaultValues: defaultFormValues,
  });

  const { control, reset, watch } = methods;

  const { append, remove } = useFieldArray({
    control,
    name: "companySpecificBusinessClassifications",
  });

  useEffect(() => {
    if (isOpen) {
      reset(defaultFormValues);
      remove();
      append({ companySpecificBusinessClassificationName: "" });
    }
  }, [isOpen, reset, remove, append]);

  const onSubmit = async (data: TMasterCompanySpecificIndustryCreateForm) => {
    try {
      const rs = await createCompanySpecificIndustryApi({
        companySpecificIndustry: data.companySpecificIndustry,
        companySpecificBusinessClassifications: data.companySpecificBusinessClassifications.map((item) => ({
          companySpecificBusinessClassificationName: item.companySpecificBusinessClassificationName,
        })),
      });

      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GLOBAL.MASTER_COMPANY_SPECIFIC_INDUSTRY] });

      reset(defaultFormValues);
      remove();
      append({ companySpecificBusinessClassificationName: "" });

      onClose();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Modal width={860} title="追加" isOpen={isOpen} enableFooter={false} onClose={onClose} enableCloseIcon={false}>
      <Form methods={methods} onSubmit={onSubmit} schema={masterDataCreateCompanySpecificIndustrySchema}>
        <Grid container spacing={3} py={3}>
          <Grid item xs={6}>
            <FormField control={control} name="companySpecificIndustry">
              <Input />
            </FormField>
          </Grid>
          <Grid item xs={6}></Grid>
          <Grid item xs={12}>
            <Stack gap={2}>
              <FormField>
                <FormLabel required>区分</FormLabel>
              </FormField>
              <CreateCompanySpecificIndustryFormTable />
            </Stack>
          </Grid>
        </Grid>
        <Stack flexDirection="row" justifyContent="center" gap="24px">
          <Button variant="outline" onClick={onClose}>
            キャンセル
          </Button>
          <Button type="submit" disabled={watch("companySpecificBusinessClassifications").length === 0}>
            保存
          </Button>
        </Stack>
      </Form>
    </Modal>
  );
};
